import React, { useState, useEffect, Suspense, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { 
  CheckCircle, 
  Eye, 
  EyeOff, 
  Copy, 
  X, 
  ArrowLeft, 
  Loader2, 
  Building2, 
  Truck, 
  Ambulance, 
  UserPlus,
  LayoutDashboard,
  BarChart3,
  ShieldCheck,
  Hospital,
  Users,
  UserCheck,
  FileText,
  Car,
  Shield,
  Sliders,
  FileCheck,
  Percent,
  SlidersHorizontal,
  Settings,
  User,
  Wallet,
  LifeBuoy
} from 'lucide-react'
import OverviewCards from '../components/dashboard/OverviewCards'
import CompaniesTable from '../components/dashboard/CompaniesTable'
import UsersTable from '../components/dashboard/UsersTable'
import SuperAdminPlatformChart from '../components/dashboard/SuperAdminPlatformChart'
import EnhancedAnalyticsDashboard from '../components/dashboard/EnhancedAnalyticsDashboard'
import AdminAnalyticsDashboard from '../components/analytics/AdminAnalyticsDashboard'
import LiveMap from '../components/tracking/LiveMap'
import Sidebar from '../components/Sidebar'
import { UniversalTopBar } from '../components/Topbar'
import HospitalRegistrationForm from '../components/registration/HospitalRegistrationForm'
import FleetOperatorRegistrationForm from '../components/registration/FleetOperatorRegistrationForm'
import AdminDriverRegistrationModal from '../components/superadmin/AdminDriverRegistrationModal'
import AdminAmbulanceRegistrationModal from '../components/superadmin/AdminAmbulanceRegistrationModal'
import { FloatingNavBar } from '../components/Navbar'
import ProfilePage from './ProfilePage'
import ProfileScreen from './ProfileScreen'
import SuperAdminMaster from './SuperAdminMaster'

// ... other imports

// Lazy load components for better performance
const AmbulanceInstantBooking = React.lazy(() => import('./AmbulanceInstantBooking'))
const TrackBookings = React.lazy(() => import('../components/ambulance/TrackBookings'))
const BookingHistory = React.lazy(() => import('../components/ambulance/BookingHistory'))
const VehiclesPage = React.lazy(() => import('./VehiclesPage'))
const FleetApprovalScreen = React.lazy(() => import('../components/superadmin/FleetApprovalScreen'))
const HospitalApprovalScreen = React.lazy(() => import('../components/superadmin/HospitalApprovalScreen'))
const SuperAdminApprovalsQueue = React.lazy(() => import('./SuperAdminApprovalsQueue'))
// const MasterPricingPage = React.lazy(() => import('./MasterPricingPage'))
const AdvMasterPricingPage = React.lazy(() => import('./AdvMasterPricingPage'))
const MasterAdjustmentPage = React.lazy(() => import('./MasterAdjustmentPage'))
const AdjustmentRequestPage = React.lazy(() => import('./AdjustmentRequestPage'))
const BookingFormOptionsPage = React.lazy(() => import('./BookingFormOptionsPage'))
const DriversManagement = React.lazy(() => import('../components/superadmin/DriversManagement')) // Added import
const DriverVehicleRequestsScreen = React.lazy(() => import('../components/superadmin/DriverVehicleRequestsScreen'))
const HospitalRequestsScreen = React.lazy(() => import('../components/superadmin/HospitalRequestsScreen'))
const FleetWalletsAdminSection = React.lazy(() => import('../components/superadmin/wallet/FleetWalletsAdminSection'))
const AdminSettingsPage = React.lazy(() => import('./AdminSettingsPage'))
const SuperAdminSupportTickets = React.lazy(() => import('./SuperAdminSupportTickets'))

const SuperAdminDashboard = ({ dashboardType = 'admin' }) => {
  // ... state and handlers
  const [isUserTypeModalOpen, setIsUserTypeModalOpen] = useState(false)
  const [registeredOrganizations, setRegisteredOrganizations] = useState([])
  const [successMessage, setSuccessMessage] = useState('')
  const [showTempPassword, setShowTempPassword] = useState({})
  const [selectedSidebarItem, setSelectedSidebarItem] = useState('dashboard')
  const [showRegistrationForm, setShowRegistrationForm] = useState(null) // 'hospital', 'fleet', or null
  const [isFormLoading, setIsFormLoading] = useState(false)
  const [showBookingScreen, setShowBookingScreen] = useState(false)
  const [activeBookingTab, setActiveBookingTab] = useState('book_ambulance') // For booking navbar tabs
  const [trackingBookingId, setTrackingBookingId] = useState(null) // For auto-loading specific booking
  const [showVehiclesPage, setShowVehiclesPage] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false) // Mobile menu state
  const navigate = useNavigate()
  const location = useLocation()

  // Handle track booking from success modal
  const handleTrackBookingFromSuccess = useCallback((bookingId) => {
    console.log('Track booking clicked for:', bookingId);
    setTrackingBookingId(bookingId);
    setActiveBookingTab('track_booking');
    setSelectedSidebarItem('book_ambulance');
    setShowBookingScreen(false);
  }, []);

  // Custom sidebar item change handler
  const handleSidebarItemChange = useCallback((itemId) => {
    setSelectedSidebarItem(itemId)
    setShowBookingScreen(false)
  }, [])

  // Custom menu items for Super Admin
  const menuItems = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { id: 'book_ambulance', icon: Truck, label: 'Book Ambulance' },
    { id: 'analytics', icon: BarChart3, label: 'Analytics' },
    { id: 'hospitals', icon: Building2, label: 'Hospitals' },
    { id: 'onboarding_approvals', icon: FileCheck, label: 'Onboarding Approvals' },
    { id: 'fleet_approvals', icon: ShieldCheck, label: 'Fleet Approvals' },
    { id: 'fleet_wallets', icon: Wallet, label: 'Fleet Wallets' },
    { id: 'hospital_approvals', icon: Hospital, label: 'Hospital Approvals' },
    { id: 'operators', icon: UserPlus, label: 'Operators' },
    { id: 'drivers', icon: Users, label: 'Drivers' },
    { id: 'driver_vehicle_requests', icon: UserCheck, label: 'Driver/Vehicle Requests' },
    { id: 'hospital_requests', icon: FileText, label: 'Hospital Requests' },
    { id: 'all_vehicles', icon: Car, label: 'All Vehicles' },
    { id: 'super_admin_master', icon: Shield, label: 'Super Admin Master' },
    { id: 'master_adjustment', icon: Sliders, label: 'Master Adjustment' },
    { id: 'adjustment_request', icon: FileCheck, label: 'Adjustment Request' },
    { id: 'adv_master_pricing', icon: Percent, label: 'Adv. Master Pricing' },
    { id: 'booking_form_options', icon: SlidersHorizontal, label: 'Booking Master Fields' },
    { id: 'support_tickets', icon: LifeBuoy, label: 'Support Tickets' },
    { id: 'settings', icon: Settings, label: 'Settings' },
    { id: 'profile', icon: User, label: 'Profile' }
  ]

  // Handle registration success data from location state
  useEffect(() => {
    if (location.state?.success) {
      setSuccessMessage(location.state.message)
      setRegisteredOrganizations(location.state.organizations || [])
      setIsUserTypeModalOpen(false)

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000)
    }
  }, [location.state])

  const handleHospitalRegistrationClick = () => {
    // Show hospital registration form directly in dashboard
    setShowRegistrationForm('hospital')
    setSelectedSidebarItem('registration')
  }

  const handleFleetOperatorRegistrationClick = () => {
    // Show fleet operator registration form directly in dashboard
    setShowRegistrationForm('fleet')
    setSelectedSidebarItem('registration')
  }

  const handleBackToDashboard = () => {
    setShowRegistrationForm(null)
    setSelectedSidebarItem('dashboard')
    setSuccessMessage('')
  }

  const handleRegistrationSuccess = (result) => {
    const organizationType = showRegistrationForm === 'hospital' ? 'Hospital' : 'Fleet Operator';
    const organizationName = result.data?.hospital?.name || result.data?.operator?.name || 'Organization';

    const successMsg = `${organizationType} "${organizationName}" has been registered successfully! The admin user can now login with their credentials.`;

    setSuccessMessage(successMsg);

    // Clear success message after 8 seconds
    setTimeout(() => {
      setSuccessMessage('');
    }, 8000);
  }

  const handleRegistrationError = (error) => {
    setSuccessMessage('');
    // You can add error handling here if needed
    console.error('Registration error:', error);
  }

  const togglePasswordVisibility = (orgId) => {
    setShowTempPassword(prev => ({
      ...prev,
      [orgId]: !prev[orgId]
    }))
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    // Could add a toast notification here
  }

  const dismissSuccessMessage = () => {
    setSuccessMessage('')
  }

  const handleSuperAdminMasterSuccess = (message) => {
    setSuccessMessage(message)
    setTimeout(() => setSuccessMessage(''), 8000)
  }

  // Helper function to get active tab display name
  const getActiveTabDisplayName = (activeTab) => {
    if (activeTab === 'book_ambulance') {
      return 'Instant Booking';
    }
    if (activeTab === 'track_booking') {
      return 'Track Booking';
    }
    return 'Booking History';
  };

  // Helper function to get registration form title
  const getRegistrationFormTitle = (formType) => {
    return formType === 'hospital' ? 'Hospital Registration' : 'Fleet Operator Registration';
  };

  // Helper function to get registration form description
  const getRegistrationFormDescription = (formType) => {
    return formType === 'hospital'
      ? 'Register a new hospital with admin user account.'
      : 'Register a new fleet operator with admin user account.';
  };

  const renderContent = () => {
    switch (selectedSidebarItem) {
      case 'dashboard':
        return (
          <div className="space-y-4 sm:space-y-6">
            {/* Action Buttons - Only on dashboard */}
            <header className="mb-4 sm:mb-6 flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                <Button
                  className="text-xs sm:text-sm w-full sm:w-auto flex items-center gap-2"
                  onClick={() => setShowBookingScreen(true)}
                >
                  <Ambulance className="w-4 h-4" />
                  Book Ambulance
                </Button>
                <Button
                  className="text-xs sm:text-sm w-full sm:w-auto flex items-center gap-2"
                  onClick={handleHospitalRegistrationClick}
                >
                  <Building2 className="w-4 h-4" />
                  Hospital Registration
                </Button>
                <Button
                  className="text-xs sm:text-sm w-full sm:w-auto flex items-center gap-2"
                  onClick={handleFleetOperatorRegistrationClick}
                >
                  <Truck className="w-4 h-4" />
                  Fleet Operator Registration
                </Button>
                <AdminDriverRegistrationModal
                  onSuccess={() => { setSuccessMessage('Driver registered successfully!'); setTimeout(() => setSuccessMessage(''), 8000) }}
                  onError={(message) => window.alert(message)}
                >
                  <Button className="text-xs sm:text-sm w-full sm:w-auto flex items-center gap-2">
                    <UserPlus className="w-4 h-4" />
                    Driver Registration
                  </Button>
                </AdminDriverRegistrationModal>
                <AdminAmbulanceRegistrationModal
                  onSuccess={() => { setSuccessMessage('Ambulance registered successfully!'); setTimeout(() => setSuccessMessage(''), 8000) }}
                  onError={(message) => window.alert(message)}
                >
                  <Button className="text-xs sm:text-sm w-full sm:w-auto flex items-center gap-2">
                    <Truck className="w-4 h-4" />
                    Vehicle Registration
                  </Button>
                </AdminAmbulanceRegistrationModal>
              </div>
            </header>

            <OverviewCards />

            <Card>
              <CardHeader className="px-3 sm:px-4 md:px-6 pt-3 sm:pt-4 md:pt-6">
                <CardTitle className="font-serif text-sm sm:text-base md:text-lg">Platform Analytics</CardTitle>
                <CardDescription className="text-xs sm:text-sm">Key metrics and trends across the platform.</CardDescription>
              </CardHeader>
              <CardContent className="px-3 sm:px-4 md:px-6 pb-3 sm:pb-4 md:pb-6">
                <SuperAdminPlatformChart />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="px-3 sm:px-4 md:px-6 pt-3 sm:pt-4 md:pt-6">
                <CardTitle className="font-serif text-sm sm:text-base md:text-lg">Recent Activity</CardTitle>
                <CardDescription className="text-xs sm:text-sm">Latest actions and system events.</CardDescription>
              </CardHeader>
              <CardContent className="px-3 sm:px-4 md:px-6 pb-3 sm:pb-4 md:pb-6">
                <div className="space-y-2 sm:space-y-3">
                  {/* Show recently registered organizations in activity feed */}
                  {registeredOrganizations.slice(0, 3).map((org) => (
                    <div key={`activity-${org.id}`} className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg bg-green-50 border border-green-200">
                      <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm break-words">
                          New {org.type === 'hospital' ? 'hospital' : 'fleet operator'} "{org.name}" registered
                        </p>
                        <p className="text-xs text-muted-foreground">Just now</p>
                      </div>
                    </div>
                  ))}

                  {/* Static activity items */}
                  <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg bg-muted/50">
                    <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm break-words">New company "MediCare Plus" registered</p>
                      <p className="text-xs text-muted-foreground">2 hours ago</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg bg-muted/50">
                    <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm break-words">Driver "Rajesh Kumar" completed 50 trips milestone</p>
                      <p className="text-xs text-muted-foreground">4 hours ago</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg bg-muted/50">
                    <div className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0"></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm break-words">System maintenance scheduled for tonight</p>
                      <p className="text-xs text-muted-foreground">6 hours ago</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )

      // case 'revenue':
      //   return (
      //     <ComingSoon 
      //       title="Revenue Dashboard"
      //       description="Advanced revenue analytics, financial reporting, and revenue management features are being developed. This will include comprehensive revenue tracking, analytics, and reporting capabilities."
      //       featureName="Revenue Analytics"
      //     />
      //   )

      case 'book_ambulance':
        return (
          <div className="relative space-y-4 sm:space-y-6 pb-24">
            {/* Booking Content based on active tab */}
            {activeBookingTab === 'book_ambulance' && (
              <div className="mt-1">
                <Suspense fallback={
                  <div className="flex items-center justify-center py-6 sm:py-8">
                    <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                    <span className="text-gray-600 text-sm sm:text-base">Loading instant booking...</span>
                  </div>
                }>
                  <AmbulanceInstantBooking
                    layout="default"
                    onBookingChange={(data) => console.log('Booking data:', data)}
                    onTrackBookingClick={handleTrackBookingFromSuccess}
                  />
                </Suspense>
              </div>
            )}

            {activeBookingTab === 'track_booking' && (
              <div className="mt-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <Suspense fallback={
                    <div className="flex items-center justify-center py-6 sm:py-8">
                      <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                      <span className="text-gray-600 text-sm sm:text-base">Loading track bookings...</span>
                    </div>
                  }>
                    <TrackBookings initialBookingId={trackingBookingId} />
                  </Suspense>
                </div>
              </div>
            )}

            {activeBookingTab === 'booking_history' && (
              <div className="mt-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <Suspense fallback={
                    <div className="flex items-center justify-center py-6 sm:py-8">
                      <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                      <span className="text-gray-600 text-sm sm:text-base">Loading booking history...</span>
                    </div>
                  }>
                    <BookingHistory />
                  </Suspense>
                </div>
              </div>
            )}

            {/* Floating Booking Navbar - Fixed at bottom center */}
            <FloatingNavBar
              tabs={['Instant Booking', 'Track Booking', 'Booking History']}
              activeTab={getActiveTabDisplayName(activeBookingTab)}
              onTabChange={(tab) => {
                const tabMap = {
                  'Instant Booking': 'book_ambulance',
                  'Track Booking': 'track_booking',
                  'Booking History': 'booking_history'
                };
                const newTab = tabMap[tab];
                setActiveBookingTab(newTab);
                // Clear tracking booking ID when switching away from track booking
                if (newTab !== 'track_booking') {
                  setTrackingBookingId(null);
                }
              }}
            />
          </div>
        )

      // case 'operations':
      //   return (
      //     <div className="space-y-4">
      //       <Card>
      //         <CardHeader className="px-3 sm:px-4 md:px-6 pt-3 sm:pt-4 md:pt-6">
      //           <CardTitle className="font-serif text-sm sm:text-base md:text-lg">Live Operations</CardTitle>
      //           <CardDescription className="text-xs sm:text-sm">Real-time ambulance tracking and emergency management.</CardDescription>
      //         </CardHeader>
      //         <CardContent className="px-3 sm:px-4 md:px-6 pb-3 sm:pb-4 md:pb-6">
      //           <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4">
      //             <div className="lg:col-span-2">
      //               <LiveMap hideActiveTrips={true} />
      //             </div>
      //             <div className="space-y-3 sm:space-y-4">
      //               <Card>
      //                 <CardHeader className="px-3 sm:px-4 pt-3 sm:pt-4">
      //                   <CardTitle className="text-sm sm:text-base md:text-lg">Active Ambulances</CardTitle>
      //                 </CardHeader>
      //                 <CardContent className="px-3 sm:px-4 pb-3 sm:pb-4">
      //                   <div className="space-y-2">
      //                     <div className="flex items-center gap-2">
      //                       <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></div>
      //                       <span className="text-xs sm:text-sm break-words">AMB-001 - En route</span>
      //                     </div>
      //                     <div className="flex items-center gap-2">
      //                       <div className="w-2 h-2 bg-yellow-500 rounded-full flex-shrink-0"></div>
      //                       <span className="text-xs sm:text-sm break-words">AMB-005 - At hospital</span>
      //                     </div>
      //                     <div className="flex items-center gap-2">
      //                       <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></div>
      //                       <span className="text-xs sm:text-sm break-words">AMB-012 - En route</span>
      //                     </div>
      //                   </div>
      //                 </CardContent>
      //               </Card>
      //               <Card>
      //                 <CardHeader className="px-3 sm:px-4 pt-3 sm:pt-4">
      //                   <CardTitle className="text-sm sm:text-base md:text-lg">Emergency Alerts</CardTitle>
      //                 </CardHeader>
      //                 <CardContent className="px-3 sm:px-4 pb-3 sm:pb-4">
      //                   <div className="space-y-2">
      //                     <div className="p-2 bg-red-50 border border-red-200 rounded text-xs sm:text-sm break-words">
      //                       🚨 High priority request - Cardiac emergency
      //                     </div>
      //                     <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-xs sm:text-sm break-words">
      //                       ⚠️ Traffic delay on Route 24
      //                     </div>
      //                   </div>
      //                 </CardContent>
      //               </Card>
      //             </div>
      //           </div>
      //         </CardContent>
      //       </Card>
      //     </div>
      //   )
      case 'analytics':
        return (
          <div className="space-y-4">
            <AdminAnalyticsDashboard />
          </div>
        )
      case 'hospitals':
        return (
          <div className="space-y-4">
            <Card>
              <CardHeader className="flex flex-col gap-3 sm:gap-4 px-3 sm:px-4 md:px-6 pt-3 sm:pt-4 md:pt-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                  <div>
                    <CardTitle className="font-serif text-sm sm:text-base md:text-lg">Hospitals</CardTitle>
                    <CardDescription className="text-xs sm:text-sm">All hospitals on the platform.</CardDescription>
                  </div>
                  <Button className="text-xs sm:text-sm w-full sm:w-auto" onClick={handleHospitalRegistrationClick}>Add Hospital</Button>
                </div>
              </CardHeader>
              <CardContent className="px-3 sm:px-4 md:px-6 pb-3 sm:pb-4 md:pb-6">
                <CompaniesTable type="hospitals" />
              </CardContent>
            </Card>
          </div>
        )
      case 'fleet_approvals':
        return (
          <div className="space-y-4">
            <Suspense fallback={
              <div className="flex items-center justify-center py-6 sm:py-8">
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                <span className="text-gray-600 text-sm sm:text-base">Loading fleet approvals...</span>
              </div>
            }>
              <FleetApprovalScreen />
            </Suspense>
          </div>
        )

      case 'fleet_wallets':
        return (
          <div className="space-y-4">
            <Suspense fallback={
              <div className="flex items-center justify-center py-6 sm:py-8">
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                <span className="text-gray-600 text-sm sm:text-base">Loading fleet wallets...</span>
              </div>
            }>
              <FleetWalletsAdminSection />
            </Suspense>
          </div>
        )

      case 'onboarding_approvals':
        return (
          <div className="space-y-4">
            <Suspense fallback={
              <div className="flex items-center justify-center py-6 sm:py-8">
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                <span className="text-gray-600 text-sm sm:text-base">Loading onboarding approvals...</span>
              </div>
            }>
              <SuperAdminApprovalsQueue />
            </Suspense>
          </div>
        )

      case 'hospital_approvals':
        return (
          <div className="space-y-4">
            <Suspense fallback={
              <div className="flex items-center justify-center py-6 sm:py-8">
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                <span className="text-gray-600 text-sm sm:text-base">Loading user creation requests...</span>
              </div>
            }>
              <HospitalApprovalScreen />
            </Suspense>
          </div>
        )

      case 'operators':
        return (
          <div className="space-y-4">
            <Card>
              <CardHeader className="flex flex-col gap-3 sm:gap-4 px-3 sm:px-4 md:px-6 pt-3 sm:pt-4 md:pt-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                  <div>
                    <CardTitle className="font-serif text-sm sm:text-base md:text-lg">Fleet Operators</CardTitle>
                    <CardDescription className="text-xs sm:text-sm">All fleet operators on the platform.</CardDescription>
                  </div>
                  <Button className="text-xs sm:text-sm w-full sm:w-auto" onClick={handleFleetOperatorRegistrationClick}>Add Operator</Button>
                </div>
              </CardHeader>
              <CardContent className="px-3 sm:px-4 md:px-6 pb-3 sm:pb-4 md:pb-6">
                <CompaniesTable type="operators" />
              </CardContent>
            </Card>
          </div>
        )
      case 'drivers':
        return (
          <div className="space-y-4">
            <Suspense fallback={
              <div className="flex items-center justify-center py-6 sm:py-8">
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                <span className="text-gray-600 text-sm sm:text-base">Loading drivers...</span>
              </div>
            }>
              <DriversManagement />
            </Suspense>
          </div>
        )
      case 'driver_vehicle_requests':
        return (
          <div className="space-y-4">
            <Suspense fallback={
              <div className="flex items-center justify-center py-6 sm:py-8">
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                <span className="text-gray-600 text-sm sm:text-base">Loading driver/vehicle requests...</span>
              </div>
            }>
              <DriverVehicleRequestsScreen
                onAction={(message) => { setSuccessMessage(message); setTimeout(() => setSuccessMessage(''), 8000); }}
                onError={(message) => window.alert(message)}
              />
            </Suspense>
          </div>
        )
      case 'hospital_requests':
        return (
          <div className="space-y-4">
            <Suspense fallback={
              <div className="flex items-center justify-center py-6 sm:py-8">
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                <span className="text-gray-600 text-sm sm:text-base">Loading hospital requests...</span>
              </div>
            }>
              <HospitalRequestsScreen
                onAction={(message) => { setSuccessMessage(message); setTimeout(() => setSuccessMessage(''), 8000); }}
                onError={(message) => window.alert(message)}
              />
            </Suspense>
          </div>
        )
      // case 'vehicles':
      //   return (
      //     <div className="space-y-4">
      //       <Card>
      //         <CardHeader className="flex flex-col gap-3 sm:gap-4 px-3 sm:px-4 md:px-6 pt-3 sm:pt-4 md:pt-6">
      //           <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
      //             <div>
      //               <CardTitle className="font-serif text-sm sm:text-base md:text-lg">Vehicles</CardTitle>
      //               <CardDescription className="text-xs sm:text-sm">All ambulances in the fleet network.</CardDescription>
      //             </div>
      //             <Button className="text-xs sm:text-sm w-full sm:w-auto">Add Vehicle</Button>
      //           </div>
      //         </CardHeader>
      //         <CardContent className="px-3 sm:px-4 md:px-6 pb-3 sm:pb-4 md:pb-6">
      //           <p className="text-sm text-muted-foreground">Vehicle management interface coming soon...</p>
      //         </CardContent>
      //       </Card>
      //     </div>
      //   )
      // case 'user':
      //   return (
      //     <div className="space-y-4">
      //       <Card>
      //         <CardHeader className="flex flex-col gap-3 sm:gap-4 px-3 sm:px-4 md:px-6 pt-3 sm:pt-4 md:pt-6">
      //           <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
      //             <div className="w-full">
      //               <CardTitle className="font-serif text-sm sm:text-base md:text-lg">Users</CardTitle>
      //               <CardDescription className="text-xs sm:text-sm">All users across companies with trip stats.</CardDescription>
      //             </div>
      //             <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
      //               <Button variant="secondary" className="text-xs sm:text-sm w-full sm:w-auto">Bulk Upload</Button>
      //               <Button className="text-xs sm:text-sm w-full sm:w-auto">Add User</Button>
      //             </div>
      //           </div>
      //         </CardHeader>
      //         <CardContent className="px-3 sm:px-4 md:px-6 pb-3 sm:pb-4 md:pb-6">
      //           <UsersTable />
      //         </CardContent>
      //       </Card>
      //     </div>
      //   )
      case 'settings':
        return (
          <div className="space-y-4">
            <Suspense fallback={
              <div className="flex items-center justify-center py-6 sm:py-8">
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                <span className="text-gray-600 text-sm sm:text-base">Loading settings...</span>
              </div>
            }>
              <AdminSettingsPage />
            </Suspense>
          </div>
        )
      case 'profile':
        return (
          <div className="space-y-3 sm:space-y-4">
            <ProfileScreen
              user={{
                name: 'Mathew Norm',
                smartId: 'Smart Id',
                initials: 'MN',
                avatar: null
              }}
              onEditProfile={() => {
                console.log('Edit profile clicked in Super Admin Dashboard')
              }}
              onWalletClick={() => {
                console.log('Wallet clicked in Super Admin Dashboard')
              }}
              onFeedbackClick={() => {
                console.log('Feedback & Support clicked in Super Admin Dashboard')
              }}
              onTermsClick={() => {
                console.log('Terms & Conditions clicked in Super Admin Dashboard')
              }}
              onAboutClick={() => {
                console.log('About Us clicked in Super Admin Dashboard')
              }}
              onNotificationClick={() => {
                console.log('Notifications clicked in Super Admin Dashboard')
              }}
            />
          </div>
        )
      case 'registration':
        return (
          <div className="space-y-4">
            <Card>
              <CardHeader className="px-3 sm:px-4 md:px-6 pt-3 sm:pt-4 md:pt-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                  <div>
                    <CardTitle className="font-serif text-sm sm:text-base md:text-lg">
                      {getRegistrationFormTitle(showRegistrationForm)}
                    </CardTitle>
                    <CardDescription className="text-xs sm:text-sm">
                      {getRegistrationFormDescription(showRegistrationForm)}
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    onClick={handleBackToDashboard}
                    className="flex items-center gap-2 text-xs sm:text-sm w-full sm:w-auto"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Dashboard
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="px-3 sm:px-4 md:px-6 pb-3 sm:pb-4 md:pb-6">
                {showRegistrationForm === 'hospital' && (
                  <HospitalRegistrationForm
                    onSuccess={handleRegistrationSuccess}
                    onError={handleRegistrationError}
                    isLoading={isFormLoading}
                    setIsLoading={setIsFormLoading}
                  />
                )}
                {showRegistrationForm === 'fleet' && (
                  <FleetOperatorRegistrationForm
                    onSuccess={handleRegistrationSuccess}
                    onError={handleRegistrationError}
                    isLoading={isFormLoading}
                    setIsLoading={setIsFormLoading}
                  />
                )}
              </CardContent>
            </Card>
          </div>
        )

      case 'all_vehicles':
        return (
          <div className="space-y-4">
            <Suspense fallback={
              <div className="flex items-center justify-center py-6 sm:py-8">
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                <span className="text-gray-600 text-sm sm:text-base">Loading vehicles...</span>
              </div>
            }>
              <VehiclesPage hideDateFilter={true} />
            </Suspense>
          </div>
        )

      case 'super_admin_master':
        return <SuperAdminMaster onSuccessMessage={handleSuperAdminMasterSuccess} />

      // case 'master_pricing':
      //   return (
      //     <Suspense fallback={
      //       <div className="flex items-center justify-center py-6 sm:py-8">
      //         <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
      //         <span className="text-gray-600 text-sm sm:text-base">Loading Master Pricing...</span>
      //       </div>
      //     }>
      //       <MasterPricingPage />
      //     </Suspense>
      //   )

      case 'master_adjustment':
        return (
          <Suspense fallback={
            <div className="flex items-center justify-center py-6 sm:py-8">
              <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
              <span className="text-gray-600 text-sm sm:text-base">Loading Master Adjustment...</span>
            </div>
          }>
            <MasterAdjustmentPage />
          </Suspense>
        )

      case 'adjustment_request':
        return (
          <Suspense fallback={
            <div className="flex items-center justify-center py-6 sm:py-8">
              <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
              <span className="text-gray-600 text-sm sm:text-base">Loading Adjustment Request...</span>
            </div>
          }>
            <AdjustmentRequestPage />
          </Suspense>
        )

      case 'adv_master_pricing':
        return (
          <Suspense fallback={
            <div className="flex items-center justify-center py-6 sm:py-8">
              <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
              <span className="text-gray-600 text-sm sm:text-base">Loading Adv. Master Pricing...</span>
            </div>
          }>
            <AdvMasterPricingPage />
          </Suspense>
        )

      case 'booking_form_options':
        return (
          <Suspense fallback={
            <div className="flex items-center justify-center py-6 sm:py-8">
              <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
              <span className="text-gray-600 text-sm sm:text-base">Loading Booking Master Fields...</span>
            </div>
          }>
            <BookingFormOptionsPage />
          </Suspense>
        )

      case 'support_tickets':
        return (
          <Suspense fallback={
            <div className="flex items-center justify-center py-6 sm:py-8">
              <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
              <span className="text-gray-600 text-sm sm:text-base">Loading Support Tickets...</span>
            </div>
          }>
            <SuperAdminSupportTickets />
          </Suspense>
        )

      default:
        return null
    }
  }

  return (
    <div className="flex h-screen" style={{
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)'
    }}>
      {/* Sidebar */}
      <Sidebar
        menuItems={menuItems}
        selectedItem={selectedSidebarItem}
        onItemClick={handleSidebarItemChange}
        defaultSelectedItem="dashboard"
        isMobileMenuOpen={isMobileMenuOpen}
        onMobileMenuToggle={setIsMobileMenuOpen}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden lg:ml-0">
        {/* Topbar */}
        <div className="px-2 md:px-3 lg:px-3 relative z-30">
          <UniversalTopBar
            title="Super Admin Dashboard"
            subtitle="Manage companies, users, finance, and live operations."
            userRole="super_admin"
            onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            isMobileMenuOpen={isMobileMenuOpen}
          />
        </div>

        <main className="flex-1 overflow-y-auto px-2 md:px-3 lg:px-3 pt-2.5 pb-4">
          {/* Success Message Banner */}
          {successMessage && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start justify-between">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-green-800 font-medium">{successMessage}</p>
                  <p className="text-green-700 text-sm mt-1">
                    The user can now login with their temporary password and will be prompted to reset it on first login.
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={dismissSuccessMessage}
                className="text-green-600 hover:text-green-800"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Recently Registered Organizations */}
          {registeredOrganizations.length > 0 && (
            <Card className="mb-4 sm:mb-6">
              <CardHeader className="px-3 sm:px-4 md:px-6 pt-3 sm:pt-4 md:pt-6">
                <CardTitle className="font-serif text-sm sm:text-base md:text-lg">Recently Registered Organizations</CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  Organizations registered in this session with their temporary login credentials.
                </CardDescription>
              </CardHeader>
              <CardContent className="px-3 sm:px-4 md:px-6 pb-3 sm:pb-4 md:pb-6">
                <div className="space-y-3 sm:space-y-4">
                  {registeredOrganizations.map((org) => (
                    <div key={org.id} className="border rounded-lg p-3 sm:p-4 bg-blue-50/50">
                      <div className="flex flex-col gap-3 sm:gap-4">
                        <div className="flex-1">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium w-fit ${org.type === 'hospital'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-green-100 text-green-800'
                              }`}>
                              {org.type === 'hospital' ? '🏥 Hospital' : '🚛 Fleet Operator'}
                            </span>
                            <span className="text-sm font-medium break-words">{org.name}</span>
                          </div>
                          <div className="text-xs text-gray-600 space-y-1">
                            <p><strong>Admin:</strong> <span className="break-words">{org.adminName}</span></p>
                            <p><strong>Email:</strong> <span className="break-all">{org.adminEmail}</span></p>
                            <p><strong>Phone:</strong> <span className="break-words">{org.adminPhone}</span></p>
                          </div>
                        </div>
                        <div className="bg-white border rounded-lg p-3 w-full">
                          <p className="text-xs font-medium text-gray-700 mb-2">Temporary Login Credentials:</p>
                          <div className="space-y-2">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                              <span className="text-xs text-gray-600 w-16 flex-shrink-0">Email:</span>
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <code className="text-xs bg-gray-100 px-2 py-1 rounded flex-1 break-all">{org.adminEmail}</code>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => copyToClipboard(org.adminEmail)}
                                  className="h-6 w-6 p-0 flex-shrink-0"
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                              <span className="text-xs text-gray-600 w-16 flex-shrink-0">Password:</span>
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <code className="text-xs bg-gray-100 px-2 py-1 rounded flex-1 font-mono">
                                  {showTempPassword[org.id] ? org.temporaryPassword : '••••••••'}
                                </code>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => togglePasswordVisibility(org.id)}
                                  className="h-6 w-6 p-0 flex-shrink-0"
                                >
                                  {showTempPassword[org.id] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => copyToClipboard(org.temporaryPassword)}
                                  className="h-6 w-6 p-0 flex-shrink-0"
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          </div>
                          <p className="text-xs text-amber-600 mt-2 font-medium">
                            ⚠️ User must reset password on first login
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}


          {/* Booking Screen */}
          {showBookingScreen && (
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Book Ambulance</h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowBookingScreen(false)}
                  className="flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Close
                </Button>
              </div>
              <Suspense fallback={
                <div className="flex items-center justify-center py-6 sm:py-8">
                  <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                  <span className="text-gray-600 text-sm sm:text-base">Loading instant booking...</span>
                </div>
              }>
                <AmbulanceInstantBooking
                  layout="default"
                  onBookingChange={(data) => console.log('Booking data:', data)}
                  onTrackBookingClick={handleTrackBookingFromSuccess}
                />
              </Suspense>
            </div>
          )}

          {/* Dynamic Content based on sidebar selection */}
          {!showBookingScreen && renderContent()}
        </main>
      </div>

    </div>
  )
}

export default SuperAdminDashboard